import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from "axios";
import { toast } from "react-toastify";
import { isTokenExpired } from "lib/utils/verifyJwt";
import { store } from "lib/redux/store";
import { setAccessToken } from "lib/redux/uiSlice";

interface AxiosClientOptions {
  contentType: "application/json" | "multipart/form-data";
  authMode: "required" | "optional" | "none";
  withCredentials?: boolean;
  on401: "redirect" | "silent";
}

function createResponseErrorHandler(on401: "redirect" | "silent") {
  return (error: AxiosError) => {
    if (error.response) {
      const { status, data } = error.response;

      switch (status) {
        case 400:
          console.error("Bad request:", data);
          break;
        case 401:
          if (on401 === "redirect") {
            toast.error("Session expired. Please log in again.");
            localStorage.removeItem("access-token");
            store.dispatch(setAccessToken(null));
            setTimeout(() => {
              window.location.href = "/";
            }, 1200);
          } else {
            console.error("Unauthorized, please log in.");
            localStorage.removeItem("access-token");
          }
          break;
        case 404:
          console.error("Resource not found:", data);
          break;
        case 500:
          console.error("Server error:", data);
          break;
        default:
          console.error("Unhandled error:", data);
      }
    } else if (error.request) {
      console.error("No response received:", error.request);
    } else {
      console.error("Request setup error:", error.message);
    }
    return Promise.reject(error);
  };
}

export function createAxiosClient(options: AxiosClientOptions): AxiosInstance {
  const instance = axios.create({
    baseURL: process.env.NEXT_PUBLIC_API_BASE_URL,
    withCredentials: options.withCredentials ?? false,
    headers: {
      "Content-Type": options.contentType,
      Accept: "*/*",
    },
  });

  // Request interceptor
  instance.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      if (options.authMode === "none") {
        return config;
      }

      if (options.authMode === "required") {
        const authEndpoints = ["/auth/google-one-tap", "/auth/"];
        const isAuthEndpoint = authEndpoints.some((endpoint) =>
          config.url?.includes(endpoint)
        );

        if (isAuthEndpoint) {
          return config;
        }

        const token = localStorage.getItem("access-token");
        if (!token || isTokenExpired(token)) {
          localStorage.removeItem("access-token");
          store.dispatch(setAccessToken(null));
          return Promise.reject(
            new AxiosError(
              "Token expired. Please log in again.",
              "TOKEN_EXPIRED"
            )
          );
        }
        config.headers.Authorization = `Bearer ${token}`;
      }

      if (options.authMode === "optional") {
        const token = localStorage.getItem("access-token");
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      }

      return config;
    },
    (error) => Promise.reject(error)
  );

  // Response interceptor
  instance.interceptors.response.use(
    (response) => response,
    createResponseErrorHandler(options.on401)
  );

  return instance;
}
